// series-7 (C.3, #490, 24-sep-2026): "VER TEMAS" DE UNA PARTE, LA VENTANA DE SOLO VER.
//
// ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).
//
// Carlos (24-sep, tercera vuelta): "no es la misma ventana como tal, creala igual a
// esa pero para ver temas solamente". Asi que NO es `TopicPickerModal.js` (que no se
// toca para esto): es una copia de su estructura y de sus clases, letra por letra,
// fijadas por `TemasDeLaParteModal.test.js` contra ese fuente y contra lo que pinta.
//
// LO QUE SE COPIA: la bifurcacion por ancho en JavaScript (`useEsAncho`, el corte md:
// en el telefono la hoja de abajo, desde 768 la ventana centrada), la hoja con sus
// clases y su titulo, la ventana centrada con su velo y su panel, el visor de alto
// fijo con su tope de pantalla chica (485 px, #461; el encargo decia 383, que es un
// dato de antes de app-3), la silueta de espera de "Elige temas" dentro del visor,
// los dos avisos (#413: el de fallo y el de vacio), los renglones de tema con su
// nombre y su numero de preguntas, y el pie con el boton principal.
//
// DIFERENCIAS, Y SOLO ESTAS:
//   - sin casillas (el renglon no lleva el circulo), sin contador de elegidos y sin
//     "Limpiar" ni "Listo": el renglon no se pica (es un `div`, no un boton), pero
//     conserva sus clases;
//   - titulo C16 ("Temas") y, debajo, el nombre de la parte recortado a un renglon.
//     Va en el renglon que en "Elige temas" lleva la frase de ayuda (las mismas
//     clases, mas el recorte), asi que la ventana mide lo mismo; y como el nombre ya
//     esta arriba, dentro del visor no se repite como encabezado;
//   - un solo boton, "Cerrar" (`common:close`, el par de la casa), con las clases del
//     boton "Listo";
//   - en la ventana centrada, la X de la casa (la hoja ya trae la suya), con el panel
//     como ancla de la X y el titulo apartado de ella (el mismo arreglo de las
//     ventanas de confirmar de Pendientes);
//   - se cierra con Esc en las dos mitades (la hoja ya lo hace; en la centrada, un
//     escuchador de teclado mientras esta abierta), con la X, con "Cerrar" y con el
//     velo; al cerrar, el foco vuelve al boton que la abrio (`volverA`);
//   - la ventana centrada va en un portal al `body`: la abre una tarjeta de la lista
//     del Panel, y una capa fija dentro de la lista quedaria atada a ella.
//
// LOS TEMAS se piden AL ABRIR, nunca al montar el Panel, con la MISMA fuente que usa
// "Elige temas" para un documento: `fetchStudyTopics(userId, [docId])` de
// `pages/StudyCenter.js` (en la web, POST /study/topics; en la app sin conexion, la
// bodega). Se importa al abrir (import dinamico): el Panel no carga el Centro de
// estudio para pintar su lista, y en el paquete ya viajan juntos. Se cuentan
// PREGUNTAS (como "Elige temas" en el simulador): un tema con cero preguntas no se
// ofrece. Lo que llega bien se recuerda en memoria por documento mientras la pestana
// viva; un fallo no se recuerda (el aviso dice "vuelve a abrir esta ventana").
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { TopicPickerSkeleton } from './skeletons';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useEsAncho } from '../lib/useEsAncho';
import { MODAL_CLEARANCE_CLASS } from '../lib/bottomNavMetrics';

// Los temas que ya llegaron, por documento, mientras la pestana viva.
// remates-1-3-0b (P3, #514): el vestido del boton "Ver temas" vive aqui (antes en
// TarjetaPadreDePartes.js, que lo re-exporta): lo usan la parte, el documento normal y la card.
export const VER_TEMAS_MOVIL = 'text-[#a0a0a0] hoverable:text-white active:bg-accent active:duration-0 rounded-xl text-xs min-h-[44px] h-10 px-2';
export const VER_TEMAS_ESCRITORIO = 'text-[#a0a0a0] hoverable:text-white active:bg-accent active:duration-0 rounded-xl text-xs h-8 px-3';

const TEMAS_EN_MEMORIA = new Map();

/** Para las pruebas: olvida lo recordado. */
export function olvidarTemasDeLasPartes() {
  TEMAS_EN_MEMORIA.clear();
}

async function pedirTemas(userId, docId) {
  const { fetchStudyTopics } = await import('../pages/StudyCenter');
  return fetchStudyTopics(userId, [docId]);
}

// Los temas de ESTE documento, con al menos una pregunta, en el orden del servidor.
function temasDe(respuesta, docId) {
  const docs = respuesta && Array.isArray(respuesta.documents) ? respuesta.documents : [];
  const entrada = docs.find((d) => d && d.document_id === docId) || docs[0];
  const temas = entrada && Array.isArray(entrada.topics) ? entrada.topics : [];
  return temas.filter((tema) => tema && (tema.question_count || 0) > 0);
}

const ENFOCAR = (volverA) => {
  const el = volverA && volverA.current;
  if (el && el.isConnected && typeof el.focus === 'function') {
    el.focus();
    return true;
  }
  return false;
};

// remates-1-3-0b (P3): `temasFijos`, si viene, es la lista ya conocida (las cards del grupo traen sus
// temas con su cuenta en el material): no se pide nada. Sin el, se pide como siempre al abrir.
export default function TemasDeLaParteModal({ open, onClose, documento, userId, t, volverA, temasFijos = null }) {
  const esAncho = useEsAncho();
  const docId = documento && documento.id;
  const [carga, setCarga] = useState({ docId: null, fallo: false });

  useEffect(() => {
    if (!open || !docId) return undefined;
    if (temasFijos) return undefined;
    if (TEMAS_EN_MEMORIA.has(docId)) return undefined;
    let vivo = true;
    setCarga({ docId, fallo: false, pidiendo: true });
    pedirTemas(userId, docId)
      .then((respuesta) => {
        TEMAS_EN_MEMORIA.set(docId, temasDe(respuesta, docId));
        if (vivo) setCarga({ docId, fallo: false });
      })
      .catch(() => {
        if (vivo) setCarga({ docId, fallo: true });
      });
    return () => { vivo = false; };
  }, [open, docId, userId, temasFijos]);

  // Cerrada, se olvida el fallo: al reabrir se vuelve a pedir y no asoma el aviso viejo.
  // Si ya estaba limpio se devuelve el MISMO estado: una ventana cerrada no pide ni un
  // render de mas al montar (revision del bloque, 24-sep).
  useEffect(() => {
    if (!open) setCarga((c) => (c.docId === null && !c.fallo ? c : { docId: null, fallo: false }));
  }, [open]);

  // Cerrar la ventana centrada: avisar y devolver el foco al boton que la abrio.
  const cerrarCentrada = useCallback(() => {
    if (onClose) onClose();
    ENFOCAR(volverA);
  }, [onClose, volverA]);

  // Esc en la ventana centrada (la hoja ya lo hace sola: aqui no, o cerraria dos veces).
  useEffect(() => {
    if (!open || !esAncho) return undefined;
    const alTecla = (e) => { if (e.key === 'Escape') cerrarCentrada(); };
    document.addEventListener('keydown', alTecla);
    return () => document.removeEventListener('keydown', alTecla);
  }, [open, esAncho, cerrarCentrada]);

  if (!open || !documento) return null;

  const recordados = temasFijos || TEMAS_EN_MEMORIA.get(docId);
  const cargaFallida = !recordados && carga.docId === docId && carga.fallo;
  const loading = !recordados && !cargaFallida;
  const temas = recordados || [];
  const vacia = temas.length === 0;
  // ajustes-1-3-0 (Bloque C2, #500): T11 bajo cada tema, "{{q}} preguntas . {{f}} flashcards"
  // (singulares propios; sin flashcards, "sin flashcards"). Las cuentas ya vienen en el
  // catalogo de siempre (POST /study/topics trae `flashcard_count` por tema): cero llamadas
  // nuevas. Se arma con las tres piezas de `dashboard:partes_padre.conteo_*` y una coma (t7-3 remate R2).
  const etiquetaDeConteo = (tema) => {
    const q = tema.question_count || 0;
    const f = tema.flashcard_count || 0;
    const preguntas = t('dashboard:partes_padre.conteo_preguntas', { count: q });
    const flashcards = f > 0
      ? t('dashboard:partes_padre.conteo_flashcards', { count: f })
      : t('dashboard:partes_padre.conteo_sin_flashcards');
    return `${preguntas}, ${flashcards}`;
  };

  const cuerpo = (
    <>
      <p
        className="text-[11px] text-[#a0a0a0] leading-snug truncate"
        data-testid="temas-parte-nombre"
      >
        {documento.filename}
      </p>
      <div
        className="mt-3 h-[485px] max-h-[calc(100vh-225px-env(safe-area-inset-bottom,0px))] supports-[height:100dvh]:max-h-[calc(100dvh-225px-env(safe-area-inset-bottom,0px))] overflow-y-auto dashboard-doc-scroll"
        data-testid="temas-parte-viewport"
      >
        {loading ? (
          <TopicPickerSkeleton />
        ) : cargaFallida ? (
          <p className="text-[11px] text-[#a0a0a0] leading-snug" data-testid="temas-parte-empty-failed">
            {t('study:center.topics.empty_failed')}
          </p>
        ) : vacia ? (
          <p className="text-[11px] text-[#a0a0a0] leading-snug" data-testid="temas-parte-empty-no-topics">
            {t('study:center.topics.empty_no_topics_mode')}
          </p>
        ) : (
          <div className="space-y-1" data-testid="temas-parte-list">
            {temas.map((tema) => (
              <div
                key={tema.name}
                data-testid={`temas-parte-tema-${tema.name}`}
                className="w-full min-h-[44px] flex items-center gap-3 p-3 rounded-xl bg-[#0e0e0e] hover:bg-[#1f1f1f] active:bg-[#1f1f1f] active:duration-0 transition-colors text-left"
              >
                <span className="flex-1 min-w-0">
                  {/* ajustes-1-3-0 (Bloque C2, #500): dos renglones DE VERDAD. En "Elige temas" el
                      nombre lleva la utilidad de display en bloque junto al recorte y, en la hoja
                      compilada, esa utilidad va despues y le gana al display del recorte: el
                      recorte es inerte (CLAUDE.md, #461). Aqui va sin ella (el recorte ya pone su
                      display) y el nombre quiebra a dos renglones con "...". Desviacion DECLARADA
                      de la copia letra por letra: es la unica cadena de esta ventana que difiere.
                      (Las utilidades no se nombran aqui: regla 5.6 de la casa.) */}
                  <span className="text-sm text-white line-clamp-2">
                    {tema.name}
                  </span>
                  <span className="block text-[11px] text-[#a0a0a0] font-mono"
                    data-testid={`temas-parte-count-${tema.name}`}>
                    {etiquetaDeConteo(tema)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const pie = (cerrar) => (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
      <Button
        onClick={cerrar}
        className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-11 font-semibold px-6"
        data-testid="temas-parte-cerrar"
      >
        {t('common:close')}
      </Button>
    </div>
  );

  if (!esAncho) {
    return (
      <Sheet open onOpenChange={(abierto) => { if (!abierto && onClose) onClose(); }}>
        <SheetContent side="bottom" className="bg-[#0a0a0a] border-[#1f1f1f]"
          data-testid="temas-parte-sheet"
          aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            // La hoja devuelve el foco a su disparador; esta no tiene: se lo damos al
            // boton que la abrio.
            if (volverA && volverA.current && volverA.current.isConnected) {
              e.preventDefault();
              ENFOCAR(volverA);
            }
          }}>
          <SheetHeader>
            <SheetTitle className="text-white text-left" data-testid="temas-parte-title">
              {t('dashboard:partes_padre.temas_titulo')}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-2">
            {cuerpo}
            {pie(() => { if (onClose) onClose(); })}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 ${MODAL_CLEARANCE_CLASS} md:pb-4`}
      onClick={(e) => { e.stopPropagation(); cerrarCentrada(); }}
      data-testid="temas-parte-modal"
    >
      <div
        className="bg-[#141414] border border-[#262626] rounded-2xl w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={cerrarCentrada}
          className="modal-close-tap absolute top-4 right-4 text-[#6a6a6a] hoverable:text-white"
          aria-label={t('common:a11y.close')}
          data-testid="temas-parte-x"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-base font-semibold text-white mb-2 pr-8"
          data-testid="temas-parte-title">
          {t('dashboard:partes_padre.temas_titulo')}
        </h3>
        {cuerpo}
        {pie(cerrarCentrada)}
      </div>
    </div>,
    document.body,
  );
}
